import React, { useState } from "react";
import { ChevronDown, ChevronUp, User, Calendar, FileText } from "lucide-react";
import { Expense } from "../../../types/expenseTypes";
import StatusChip from "../chips/StatusChip";
import ApproveRejectButtons from "../buttons/ApproveRejectButtons";

interface Props {
  data: Expense[];
  selected: string[];
  onSelect: (ids: string[]) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

const columns = [
  { key: "type", label: "Type" },
  { key: "unit", label: "Unit" },
  { key: "costCenter", label: "Cost Center" },
  { key: "category", label: "Category" },
  { key: "shop", label: "Shop/Party" },
  { key: "nature", label: "Nature" },
  { key: "description", label: "Description" },
  { key: "amount", label: "Amount" },
  { key: "date", label: "Date" },
  { key: "createdBy", label: "Submitted By" },
  { key: "approvalStatus", label: "Status" },
];

const ApprovalTable: React.FC<Props> = ({ data, selected, onSelect, onApprove, onReject }) => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) onSelect(data.map(d => d.id));
    else onSelect([]);
  };

  const toggle = (id: string) => {
    onSelect(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const toggleExpand = (id: string) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const formatDate = (date: string | Date) => {
    return typeof date === "string" ? date.slice(0, 10) : new Date(date).toISOString().slice(0, 10);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            <th className="px-2 py-3 w-10">
              <input
                type="checkbox"
                onChange={toggleAll}
                checked={data.length > 0 && selected.length === data.length}
                className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                title="Select all"
              />
            </th>
            <th className="px-2 py-3 w-10"></th>
            {columns.map(c => (
              <th key={c.key} className="px-3 py-3 text-left font-semibold text-gray-700">
                {c.label}
              </th>
            ))}
            <th className="px-3 py-3 text-center font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map(row => {
            const isSelected = selected.includes(row.id);
            const isExpanded = expandedRow === row.id;

            return (
              <React.Fragment key={row.id}>
                <tr
                  className={`transition-colors ${
                    isSelected
                      ? 'bg-primary-50 hover:bg-primary-100'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="px-2 py-3 border-b">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(row.id)}
                      className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-2 py-3 border-b">
                    <button
                      onClick={() => toggleExpand(row.id)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                      title={isExpanded ? "Collapse details" : "Expand details"}
                    >
                      {isExpanded ? (
                        <ChevronUp size={16} className="text-gray-600" />
                      ) : (
                        <ChevronDown size={16} className="text-gray-600" />
                      )}
                    </button>
                  </td>
                  <td className="px-3 py-3 border-b font-medium">{row.type}</td>
                  <td className="px-3 py-3 border-b">{row.unit}</td>
                  <td className="px-3 py-3 border-b">{row.costCenter || '—'}</td>
                  <td className="px-3 py-3 border-b">{row.category || '—'}</td>
                  <td className="px-3 py-3 border-b">{row.partyName || row.shop || '—'}</td>
                  <td className="px-3 py-3 border-b">{row.nature || '—'}</td>
                  <td className="px-3 py-3 border-b max-w-xs" title={row.description || ''}>
                    {row.description
                      ? row.description.length > 40
                        ? row.description.substring(0, 40) + '...'
                        : row.description
                      : '—'}
                  </td>
                  <td className="px-3 py-3 border-b text-right font-semibold">
                    {formatCurrency(row.amount)}
                  </td>
                  <td className="px-3 py-3 border-b">{formatDate(row.date)}</td>
                  <td className="px-3 py-3 border-b">
                    <div className="flex items-center gap-1 text-gray-600">
                      <User size={14} />
                      <span className="truncate max-w-[150px]" title={row.createdBy}>
                        {row.createdBy ? row.createdBy.split('@')[0] : '—'}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 border-b">
                    <StatusChip status={row.approvalStatus || "pending"} />
                  </td>
                  <td className="px-3 py-3 border-b whitespace-nowrap">
                    <ApproveRejectButtons
                      onApprove={() => onApprove(row.id)}
                      onReject={() => onReject(row.id)}
                    />
                  </td>
                </tr>
                {isExpanded && (
                  <tr className={isSelected ? 'bg-primary-50' : 'bg-gray-50'}>
                    <td colSpan={columns.length + 3} className="px-4 py-4 border-b">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                            <FileText size={16} />
                            Expense Details
                          </h4>
                          <div className="space-y-1">
                            {(row.partyName || row.shop) && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Shop/Party:</span>
                                <span className="font-medium">{row.partyName || row.shop}</span>
                              </div>
                            )}
                            {row.partyType && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Party Type:</span>
                                <span className="font-medium capitalize">{row.partyType}</span>
                              </div>
                            )}
                            {row.name && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Name:</span>
                                <span className="font-medium">{row.name}</span>
                              </div>
                            )}
                            {row.quantity && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Quantity:</span>
                                <span className="font-medium">{row.quantity}</span>
                              </div>
                            )}
                            {row.rate && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Rate:</span>
                                <span className="font-medium">{formatCurrency(row.rate)}</span>
                              </div>
                            )}
                            {row.grossAmount && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Gross Amount:</span>
                                <span className="font-medium">{formatCurrency(row.grossAmount)}</span>
                              </div>
                            )}
                            {row.taxRate && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Tax Rate:</span>
                                <span className="font-medium">{row.taxRate}%</span>
                              </div>
                            )}
                            {row.taxAmount && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Tax Amount:</span>
                                <span className="font-medium">{formatCurrency(row.taxAmount)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                            <User size={16} />
                            Approval Information
                          </h4>
                          <div className="space-y-1">
                            {row.approvedBy && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Approved By:</span>
                                <span className="font-medium">{row.approvedBy}</span>
                              </div>
                            )}
                            {row.approvalDate && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Approval Date:</span>
                                <span className="font-medium">
                                  {formatDate(row.approvalDate)}
                                </span>
                              </div>
                            )}
                            {row.rejectedBy && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Rejected By:</span>
                                <span className="font-medium">{row.rejectedBy}</span>
                              </div>
                            )}
                            {row.rejectionReason && (
                              <div className="mt-2">
                                <span className="text-gray-600">Rejection Reason:</span>
                                <p className="font-medium mt-1">{row.rejectionReason}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                            <Calendar size={16} />
                            Timestamps
                          </h4>
                          <div className="space-y-1">
                            {row.createdAt && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Created:</span>
                                <span className="font-medium text-xs">
                                  {new Date(row.createdAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {row.updatedAt && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Updated:</span>
                                <span className="font-medium text-xs">
                                  {new Date(row.updatedAt).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {row.description && (
                              <div className="mt-2">
                                <span className="text-gray-600">Full Description:</span>
                                <p className="font-medium mt-1 text-xs">{row.description}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {data.length === 0 && (
            <tr>
              <td colSpan={columns.length + 3} className="text-center py-8 text-gray-500">
                <div className="flex flex-col items-center gap-2">
                  <FileText size={48} className="text-gray-300" />
                  <p className="text-lg font-medium">No records found</p>
                  <p className="text-sm">Try adjusting your filters</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ApprovalTable;
